import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
    Avatar,
    Box,
    ClickAwayListener,
    IconButton,
    InputAdornment,
    List,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Paper,
    Skeleton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildPath, paths } from '../routes/paths';
import { useApiClient } from '../ApiClientProvider';
import { GlobalSearchService } from '../services/globalSearchService';
import { IGlobalQuickSuggestion } from '../interfaces/IGlobalSearch';
import { addRecentSearch } from '../utils/recentSearches';
import { logEvent } from '../utils/analytics';
import { DEFAULT_AVATAR_SRC } from '../helpers/avatarColor';

const QUICK_SEARCH_DEBOUNCE_MS = 300;

const SUGGESTION_ICON: Record<IGlobalQuickSuggestion['type'], React.ReactElement> = {
    event: <EventOutlinedIcon fontSize="small" />,
    community: <GroupsOutlinedIcon fontSize="small" />,
    person: <PersonOutlineOutlinedIcon fontSize="small" />,
};

// Events/communities read as square tiles (like a poster/logo); people stay circular avatars.
// Each type keeps a distinct, theme-driven tint so the mixed dropdown list is scannable at a glance.
const SUGGESTION_AVATAR_VARIANT: Record<IGlobalQuickSuggestion['type'], 'rounded' | 'circular'> = {
    event: 'rounded',
    community: 'rounded',
    person: 'circular',
};

const SUGGESTION_AVATAR_COLOURS: Record<IGlobalQuickSuggestion['type'], { bgcolor: string; color: string }> = {
    event: { bgcolor: 'primary.lighter', color: 'primary.dark' },
    community: { bgcolor: 'secondary.lighter', color: 'secondary.dark' },
    person: { bgcolor: 'action.hover', color: 'text.secondary' },
};

/**
 * Global search input shown in the participant header. Typing shows a
 * lightweight, id/name-only typeahead dropdown (LinkedIn-style); no full
 * search runs until the user picks a suggestion, hits Enter, or clicks
 * "See all results", which is when the full aggregated /search page loads.
 */
const HeaderSearchBar: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const applicationClient = useApiClient('applicationClient');
    const searchService = useMemo(() => new GlobalSearchService(applicationClient), [applicationClient]);

    const isGlobalSearchRoute = location.pathname === paths.public.search;
    const queryFromUrl = isGlobalSearchRoute ? new URLSearchParams(location.search).get('q') ?? '' : '';
    const [value, setValue] = useState(() => queryFromUrl);
    const [suggestions, setSuggestions] = useState<IGlobalQuickSuggestion[]>([]);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const debounceRef = useRef<number | null>(null);
    const requestIdRef = useRef(0);
    const abortControllerRef = useRef<AbortController | null>(null);

    const clearDebounce = () => {
        if (debounceRef.current !== null) {
            window.clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
    };

    const abortInFlightRequest = () => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
    };

    useEffect(() => () => {
        clearDebounce();
        abortInFlightRequest();
    }, []);

    useEffect(() => {
        if (!isGlobalSearchRoute) {
            setValue('');
        }
    }, [isGlobalSearchRoute]);

    const closeDropdown = () => {
        clearDebounce();
        abortInFlightRequest();
        requestIdRef.current += 1;
        setDropdownOpen(false);
        setSuggestions([]);
        setIsLoadingSuggestions(false);
    };

    const submitSearch = (term?: string) => {
        const trimmed = (term ?? value).trim();
        if (!trimmed) return;
        closeDropdown();
        addRecentSearch(trimmed);
        logEvent('Header', 'search', trimmed);
        navigate(`${paths.public.search}?q=${encodeURIComponent(trimmed)}`);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            submitSearch();
            return;
        }
        if (event.key === 'Escape') {
            closeDropdown();
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.value;
        setValue(nextValue);

        clearDebounce();
        const trimmed = nextValue.trim();
        if (trimmed.length < 2) {
            requestIdRef.current += 1;
            abortInFlightRequest();
            setSuggestions([]);
            setIsLoadingSuggestions(false);
            setDropdownOpen(false);
            return;
        }

        setDropdownOpen(true);
        setIsLoadingSuggestions(true);
        debounceRef.current = window.setTimeout(() => {
            const requestId = ++requestIdRef.current;
            abortInFlightRequest();
            const controller = new AbortController();
            abortControllerRef.current = controller;
            searchService
                .quickSearch(trimmed, { signal: controller.signal })
                .then((results) => {
                    if (requestIdRef.current !== requestId) return;
                    setSuggestions(results);
                    setIsLoadingSuggestions(false);
                })
                .catch((error) => {
                    if (axios.isCancel(error) || controller.signal.aborted) return;
                    if (requestIdRef.current !== requestId) return;
                    console.error('Quick search failed:', error);
                    setSuggestions([]);
                    setIsLoadingSuggestions(false);
                });
        }, QUICK_SEARCH_DEBOUNCE_MS);
    };

    const handleClear = () => {
        closeDropdown();
        setValue('');
        navigate(paths.public.search);
    };

    const openSuggestion = (suggestion: IGlobalQuickSuggestion) => {
        addRecentSearch(value.trim());
        logEvent('Header', 'search-suggestion', suggestion.type);
        closeDropdown();
        setValue('');
        if (suggestion.type === 'event') {
            navigate(buildPath(paths.public.events.details, { id: suggestion.id }));
            return;
        }
        if (suggestion.type === 'community' && suggestion.slug) {
            navigate(buildPath(paths.public.communityDetails, { slug: suggestion.slug }));
            return;
        }
        navigate(`${paths.public.profile}/${encodeURIComponent(suggestion.id)}`);
    };

    const trimmedValue = value.trim();
    const showDropdown = dropdownOpen && trimmedValue.length >= 2;

    return (
        <ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
            <Box
                sx={{
                    display: { xs: 'none', lg: 'flex' },
                    alignItems: 'center',
                    ml: 2,
                    position: 'relative',
                    width: { lg: 280, xl: 280 },
                    flexShrink: 0,
                }}
            >
                <>
                    <TextField
                        inputRef={inputRef}
                        size="small"
                        placeholder="Search AttendIn"
                        value={value}
                        onChange={handleChange}
                        onFocus={() => {
                            if (trimmedValue.length >= 2) setDropdownOpen(true);
                        }}
                        onKeyDown={handleKeyDown}
                        aria-label="Search events, communities and people"
                        sx={{
                            width: '100%',
                            zIndex: 3,
                        }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: value ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" aria-label="Clear search" onClick={handleClear}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : undefined,
                                sx: {
                                    borderRadius: 999,
                                    bgcolor: 'background.paper',
                                },
                            },
                        }}
                    />

                    {showDropdown && (
                        <Paper
                            elevation={1}
                            sx={{
                                position: 'absolute',
                                top: 'calc(100% + 8px)',
                                left: 0,
                                width: { lg: 320, xl: 380 },
                                zIndex: 4,
                                borderRadius: '12px',
                                overflow: 'hidden',
                            }}
                        >
                            {isLoadingSuggestions ? (
                                <Box sx={{ p: 1.5 }}>
                                    {[0, 1, 2].map((row) => (
                                        <Box key={row} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                                            <Skeleton variant="circular" width={32} height={32} />
                                            <Skeleton variant="text" width="60%" />
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <List dense disablePadding>
                                    {suggestions.map((suggestion) => (
                                        <ListItemButton
                                            key={`${suggestion.type}-${suggestion.id}`}
                                            onClick={() => openSuggestion(suggestion)}
                                        >
                                            <ListItemAvatar sx={{ minWidth: 44 }}>
                                                <Avatar
                                                    variant={SUGGESTION_AVATAR_VARIANT[suggestion.type]}
                                                    src={suggestion.type === 'person' ? (suggestion.imageUrl || DEFAULT_AVATAR_SRC) : suggestion.imageUrl}
                                                    sx={{ width: 32, height: 32, ...SUGGESTION_AVATAR_COLOURS[suggestion.type] }}
                                                >
                                                    {suggestion.type !== 'person' && SUGGESTION_ICON[suggestion.type]}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={suggestion.label}
                                                secondary={suggestion.subtitle}
                                                slotProps={{
                                                    primary: { noWrap: true },
                                                    secondary: { noWrap: true },
                                                }}
                                            />
                                        </ListItemButton>
                                    ))}
                                    {suggestions.length === 0 && (
                                        <Box sx={{ px: 2, py: 1.5 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No quick matches for "{trimmedValue}"
                                            </Typography>
                                        </Box>
                                    )}
                                    <ListItemButton
                                        onClick={() => submitSearch()}
                                        sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                                    >
                                        <ListItemText
                                            primary={`See all results for "${trimmedValue}"`}
                                            slotProps={{ primary: { color: 'primary.main', fontWeight: 500 } }}
                                        />
                                    </ListItemButton>
                                </List>
                            )}
                        </Paper>
                    )}
                </>
            </Box>
        </ClickAwayListener>
    );
};

/** Compact icon-only trigger for small screens. */
export const HeaderSearchButton: React.FC = () => {
    const navigate = useNavigate();
    return (
        <Box sx={{ display: { xs: 'flex', lg: 'none' } }}>
            <Tooltip title="Search" enterDelay={1000}>
                <IconButton
                    aria-label="Search events, communities, people, posts and organisations"
                    onClick={() => navigate(paths.public.search)}
                >
                    <SearchIcon />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

export default HeaderSearchBar;
